import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, OctagonAlert, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui/core/IconButton';

export type BannerTone = 'info' | 'warning' | 'danger' | 'success';
export type BannerVariant = 'seamless' | 'bordered' | 'accent-left';

export type BannerProps = {
  tone?: BannerTone;
  variant?: BannerVariant;
  title: string;
  description?: string;
  action?: ReactNode;
  onDismiss?: () => void;
  className?: string;
};

const toneClassSeamless: Record<BannerTone, string> = {
  info: 'bg-info-soft border-info-soft text-info-fg',
  warning: 'bg-warning-soft border-warning-soft text-warning-fg',
  danger: 'bg-danger-soft border-danger-soft text-danger-fg',
  success: 'bg-success-soft border-success-soft text-success-fg',
};

const toneClassBordered: Record<BannerTone, string> = {
  info: 'bg-info-soft border-info text-info-fg',
  warning: 'bg-warning-soft border-warning text-warning-fg',
  danger: 'bg-danger-soft border-danger text-danger-fg',
  success: 'bg-success-soft border-success text-success-fg',
};

const toneClassAccentLeft: Record<BannerTone, string> = {
  info: 'bg-info-soft border-transparent border-l-4 border-l-info text-info-fg',
  warning: 'bg-warning-soft border-transparent border-l-4 border-l-warning text-warning-fg',
  danger: 'bg-danger-soft border-transparent border-l-4 border-l-danger text-danger-fg',
  success: 'bg-success-soft border-transparent border-l-4 border-l-success text-success-fg',
};

const toneIcon: Record<BannerTone, ReactNode> = {
  info: <Info className="size-4" aria-hidden />,
  warning: <AlertTriangle className="size-4" aria-hidden />,
  danger: <OctagonAlert className="size-4" aria-hidden />,
  success: <CheckCircle2 className="size-4" aria-hidden />,
};

export function Banner({
  tone = 'info',
  variant = 'seamless',
  title,
  description,
  action,
  onDismiss,
  className,
}: BannerProps) {
  const variantClass =
    variant === 'bordered'
      ? toneClassBordered[tone]
      : variant === 'accent-left'
      ? toneClassAccentLeft[tone]
      : toneClassSeamless[tone];

  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 rounded-card border px-4 py-3 transition-colors',
        variantClass,
        className,
      )}
    >
      <span className="mt-0.5 shrink-0">{toneIcon[tone]}</span>
      <div className="min-w-0 flex-1">
        <p className="m-0 text-body font-semibold">{title}</p>
        {description ? <p className="mt-1 mb-0 text-caption opacity-90">{description}</p> : null}
        {action ? <div className="mt-2">{action}</div> : null}
      </div>
      {onDismiss ? (
        <IconButton label="Đóng banner" icon={<X />} size="sm" onClick={onDismiss} tooltip={false} />
      ) : null}
    </div>
  );
}
